import { catchError, map, tap, Observable, of, delay, switchMap, timer } from 'rxjs';
import { ComponentDataSourceService } from '../../component-data-source.service';
import { FunctionRegistryService } from '../../../function-registry/function-registry.service';
import { EducationalPathDTO } from '../../../../dto/educational-path/educational-path.dto';
import { EducationalPathItemDTO } from '../../../../dto/educational-path/educational-path-item.dto';
import { EducationalPathVersionRequirementDTO } from '../../../../dto/educational-path/educational-path-version-requirement.dto';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';

interface PathItemResponse {
  id: number;
  type: string;
  name: string;
  created_at?: string | null;
  updated_at?: string | null;
  item_details?: any;
  version_details?: any;
  module_version_id?: number;
  subtype_version_id?: number;
  module_id?: number;
  subtype_id?: number;
  start_date?: string;
  end_date?: string;
}

export function educationalPathRequirements(componentDataSourceService: ComponentDataSourceService, functionRegistryService: FunctionRegistryService) {
  return {
    name: 'Educational Path Requirements',
    description: 'Lists all requirements for a specific educational path version',
    execute: (params?: LoadDataParams) => {
      
      // Get the version ID from the route
      const versionId = componentDataSourceService.getCurrentVersionId();
      if (!versionId) {
        console.warn('No version ID found in route');
        return of({
          items: [],
          totalCount: 0,
          pageSize: 10,
          pageIndex: 0
        });
      }

      // Use the standardized API approach like educational-paths.ts
      return componentDataSourceService['loadDataWithStandardFiltersGET'](
        'educational_path_requirements', 
        `/paths/versions/${versionId}/requirements`, 
        params
      ).pipe(
        tap(result => {
        }),
        catchError(error => {
          console.error('Error fetching educational path requirements:', error);
          return of({
            items: [],
            totalCount: 0,
            pageSize: 10,
            pageIndex: 0
          });
        }),
        map((result: any) => {
          // Support ListResponseDTO (data + meta) and legacy (requirements or items)
          let items: PathItemResponse[] = [];
          let totalCount = 0;
          let pageSize = result?.pageSize ?? result?.meta?.pageSize ?? 10;
          let pageIndex = result?.pageIndex ?? (result?.meta?.page != null ? result.meta.page - 1 : 0);

          if (result?.data && result?.meta) {
            items = result.data;
            totalCount = result.meta.totalItems ?? 0;
            pageSize = result.meta.pageSize ?? 10;
            pageIndex = result.meta.page != null ? result.meta.page - 1 : 0;
          } else if (result?.requirements) {
            items = result.requirements;
            totalCount = result.totalCount ?? 0;
            pageSize = result.pageSize ?? 10;
            pageIndex = result.pageIndex ?? 0;
          }

          if (!items.length) {
            return { items: [], totalCount: 0, pageSize, pageIndex };
          }

          try {
            const mappedItems = items.map((item: PathItemResponse) => {
              const name = item.item_details?.name || item.name || 'Unnamed';
              const itemId = item.item_details?.id ?? item.module_id ?? item.subtype_id ?? item.id;
              const type = item.item_details?.type || (item.module_id ? 'Module' : 'Subtype') || item.type || 'unknown';

              const versionStartDate = item.start_date ??
                item.version_details?.valid_from ??
                item.version_details?.start_date ??
                item.created_at ??
                '';
              const versionEndDate = item.end_date ??
                item.version_details?.valid_to ??
                item.version_details?.end_date ??
                item.updated_at ??
                '';

              return {
                ...item,
                id: item.id,
                type,
                name,
                item_id: item.id,
                version_id: item.module_version_id || item.subtype_version_id,
                start_date: versionStartDate,
                end_date: versionEndDate
              };
            });

            return {
              items: mappedItems,
              totalCount,
              pageSize,
              pageIndex
            };
          } catch (error) {
            console.error('Error converting educational path requirements:', error);
            return { items: [], totalCount: 0, pageSize, pageIndex };
          }
        })
      );
    },
    settings: {
      columns: [
        { key: 'type', header: 'Type', type: 'text', sortable: true },
        { key: 'name', header: 'Name', type: 'text', sortable: true },
        { key: 'version_id', header: 'Version ID', type: 'number', sortable: true },
        { key: 'start_date', header: 'Start dato', type: 'date', sortable: true },
        { key: 'end_date', header: 'Slut dato', type: 'date', sortable: true },
        { 
          key: 'remove',
          header: '',
          type: 'button',
          variant: 'raised',
          buttonText: 'Fjern',
          buttonColor: 'warn',
          action: (row: any) => {
            const versionId = componentDataSourceService.getCurrentVersionId();
            if (versionId && row?.id != null) {
              functionRegistryService.executeFunction('removeRequirement', [versionId, row.id]);
            }
          }
        }
      ],
      pageable: true,
      sortable: true,
      rowHover: true,
      defaultSortColumn: 'start_date',
      defaultSortDirection: 'desc'
    }
  }
} 